import { SERIES_COUNT } from '@finsim/charts';
import type { ItemId, Project } from '@finsim/model';
import { displayOrder, itemGroup, type ItemGroup } from '../../../entities/project';
import { UNCATEGORIZED, type ScenarioResult } from '../../../entities/simulation';
import type { ValueMode } from '../../../entities/view-settings';

export const seriesColor = (slot: number): string =>
  `var(--chart-series-${String((slot % SERIES_COUNT) + 1)})`;

/** A variant's colour (its categorical slot). */
export const scenarioColor = (s: { color: number }): string => seriesColor(s.color);

export interface NamedSeries {
  id: string;
  label: string;
  values: Float64Array;
  color: string;
}

const magnitude = (values: Iterable<number>): number => {
  let sum = 0;
  for (const v of values) sum += Math.abs(v);
  return sum;
};

/**
 * Signed cash flow per category (income > 0, expenses < 0) in a stable order: income categories,
 * then expenses, as listed in the project. Beyond `SERIES_COUNT - 1` categories the smallest are
 * folded into "Other" — categorical colours are never cycled.
 */
export function categorySeries(
  project: Project,
  result: ScenarioResult,
  mode: ValueMode,
  labels: { uncategorized: string; other: string },
): NamedSeries[] {
  const entries = Object.entries(result.categories).filter(([, pair]) => magnitude(pair[mode]) > 0);
  const order = new Map(
    project.categories.map((c, i) => [c.id as string, (c.kind === 'income' ? 0 : 1000) + i]),
  );
  entries.sort(([a], [b]) => (order.get(a) ?? 5000) - (order.get(b) ?? 5000));

  const named = entries.map(([id, pair]) => ({
    id,
    label:
      id === UNCATEGORIZED
        ? labels.uncategorized
        : (project.categories.find((c) => c.id === id)?.name ?? id),
    values: pair[mode],
  }));
  let kept = named;
  if (named.length > SERIES_COUNT) {
    const bySize = [...named].sort((a, b) => magnitude(b.values) - magnitude(a.values));
    const keepIds = new Set(bySize.slice(0, SERIES_COUNT - 1).map((s) => s.id));
    const rest = named.filter((s) => !keepIds.has(s.id));
    const other = new Float64Array(rest[0]?.values.length ?? 0);
    for (const s of rest) s.values.forEach((v, i) => (other[i] = (other[i] ?? 0) + v));
    kept = [
      ...named.filter((s) => keepIds.has(s.id)),
      { id: '__other', label: labels.other, values: other },
    ];
  }
  return kept.map((s, i) => ({ ...s, color: seriesColor(i) }));
}

export interface ExplainLine {
  itemId: string;
  name: string;
  group: ItemGroup;
  amount: number;
}

export interface MonthExplanation {
  opening: number;
  lines: ExplainLine[];
  overdraftInterest: number;
  closing: number;
  assets: number;
  liabilities: number;
  netWorth: number;
}

/**
 * Every cash movement of one variant in one month (nominal money, where the books balance
 * exactly): opening + Σ lines − overdraft interest = closing.
 */
export function explainMonth(
  project: Project,
  scenarioId: string,
  result: ScenarioResult,
  index: number,
): MonthExplanation {
  const at = (values: ArrayLike<number>, i: number): number => values[i] ?? 0;
  const cash = result.series.cash.nominal;
  const lines: ExplainLine[] = [];
  const scenario = project.scenarios.find((s) => s.id === scenarioId);
  if (scenario) {
    for (const item of displayOrder(project, scenario.id)) {
      const series = result.items[item.id];
      if (!series) continue;
      const amount = at(series.cash.nominal, index);
      if (amount !== 0)
        lines.push({ itemId: item.id, name: item.name, group: itemGroup(item), amount });
    }
  }
  return {
    opening: index > 0 ? at(cash, index - 1) : project.assumptions.startingCash,
    lines,
    overdraftInterest: at(result.series.overdraftInterest.nominal, index),
    closing: at(cash, index),
    assets: at(result.series.assets.nominal, index),
    liabilities: at(result.series.liabilities.nominal, index),
    netWorth: at(result.series.netWorth.nominal, index),
  };
}

export interface AccountSeries {
  id: string;
  label: string;
  values: Float64Array;
}

const owes = (payable: Float64Array): boolean => payable.some((v) => v > 0);

/**
 * Account balances of a variant (Balance view, report): assets above zero; loans and the price
 * still owed for assets bought in instalments below it.
 */
export function accountSeries(
  project: Project,
  itemIds: readonly ItemId[],
  result: ScenarioResult,
  mode: ValueMode,
  owedLabel: (name: string) => string,
): AccountSeries[] {
  return itemIds.flatMap((id): AccountSeries[] => {
    const item = project.items[id];
    const s = result.items[id];
    if (!item || !s) return [];
    if (s.type === 'asset') {
      const owed = s.payable[mode];
      const balance = { id, label: item.name, values: s.balance[mode] };
      return owes(owed)
        ? [balance, { id: `${id}:owed`, label: owedLabel(item.name), values: owed.map((v) => -v) }]
        : [balance];
    }
    if (s.type === 'loan')
      return [{ id, label: item.name, values: s.balance[mode].map((v) => -v) }];
    return [];
  });
}

export interface DebtSeries {
  id: string;
  /** The loan, or the asset whose price is owed. */
  itemId: ItemId;
  label: string;
  balance: Float64Array;
  /** Monthly interest and principal paid (zero for a price owed, which is interest-free). */
  interest: Float64Array;
  principal: Float64Array;
}

/** A variant's debts (Debt view, report): loans, and the price still owed to sellers. */
export function debtSeries(
  project: Project,
  itemIds: readonly ItemId[],
  result: ScenarioResult,
  mode: ValueMode,
  owedLabel: (name: string) => string,
): DebtSeries[] {
  return itemIds.flatMap((id): DebtSeries[] => {
    const item = project.items[id];
    const s = result.items[id];
    if (!item || !s) return [];
    if (s.type === 'loan') {
      return [
        {
          id,
          itemId: id,
          label: item.name,
          balance: s.balance[mode],
          interest: s.interest[mode],
          principal: s.principal[mode],
        },
      ];
    }
    if (s.type === 'asset' && owes(s.payable[mode])) {
      const none = new Float64Array(s.payable[mode].length);
      return [
        {
          id: `${id}:owed`,
          itemId: id,
          label: owedLabel(item.name),
          balance: s.payable[mode],
          interest: none,
          principal: none,
        },
      ];
    }
    return [];
  });
}

/** Whether a variant ever owes anything: a loan, or an asset's price not yet paid. */
export function hasDebt(result: ScenarioResult): boolean {
  return Object.values(result.items).some(
    (i) => i.type === 'loan' || (i.type === 'asset' && owes(i.payable.nominal)),
  );
}
